import { createTheme, PaletteColorOptions, Theme } from "@mui/material";
import { ptBR } from "@mui/x-date-pickers";
import { WINDOW_BREAKPOINTS } from "src/constants/WindowBreakpoints";

import meupedagioVariables from "src/styles/partners/meupedagio.module.scss";
// import { PartnerKeys } from "src/constants/PartnerKeys";

declare module '@mui/material/styles' {
    interface Palette {
        feedback: {
            success: string;
            error: string;
            warning: string;
            info: string;
        };
        neutral: {
            100?: string;
            200?: string;
            300?: string;
            400?: string;
        }
    }

    interface PaletteOptions {
        feedback?: {
            success?: string;
            error?: string;
            warning?: string;
            info?: string;
        };
        neutral: PaletteColorOptions;
    }

    interface PaletteColor {
        100?: string;
        200?: string;
        300?: string;
        400?: string;
        500?: string;
        600?: string;
        700?: string;
        800?: string;
        900?: string;
    }

    interface TypeBackground {
        secondary?: string;
    }
}

export default function createMuiTheme() {
    // const clientId = import.meta.env.VITE_APP_CLIENT_ID;
    // const customTheme = clientId === PartnerKeys.ECONOROESTE
    //     ? ecoNoroesteVariables
    //     : clientId === PartnerKeys.EPR
    //         ? eprVariables
    //         : meupedagioVariables;

    const customTheme = meupedagioVariables;
    const theme = createTheme(customTheme ? {
        typography: {
            fontFamily: 'Lato, sans-serif'
        },
        palette: {
            primary: {
                main: customTheme['primary-default'],
                100: customTheme['primary-100'],
                200: customTheme['primary-200'],
                300: customTheme['primary-300'],
                400: customTheme['primary-400'],
                500: customTheme['primary-500'],
                600: customTheme['primary-600'],
                700: customTheme['primary-700'],
                800: customTheme['primary-800'],
            },
            secondary: {
                main: customTheme['secondary-default'],
                100: customTheme['secondary-100'],
                200: customTheme['secondary-200'],
                300: customTheme['secondary-300'],
                400: customTheme['secondary-400'],
                500: customTheme['secondary-500'],
                600: customTheme['secondary-600'],
                700: customTheme['secondary-700'],
                800: customTheme['secondary-800'],
            },
            grey: {
                100: customTheme['grey-100'],
                200: customTheme['grey-200'],
                300: customTheme['grey-300'],
                400: customTheme['grey-400'],
                500: customTheme['grey-500'],
                600: customTheme['grey-600'],
                700: customTheme['grey-700'],
                800: customTheme['grey-800'],
                900: customTheme['grey-900'],
            },
            neutral: {
                100: customTheme['neutral-100'],
                200: customTheme['neutral-200'],
                300: customTheme['neutral-300'],
                400: customTheme['neutral-400'],
            },
            background: {
                default: customTheme['overlay-100'],
                paper: customTheme['overlay-200'],
                secondary: customTheme['overlay-300'],
            },
            text: {
                primary: customTheme['text-secondary'],
                secondary: customTheme['text-secondary'],
            },
            warning: {
                main: customTheme['warning-main'],
                light: customTheme['warning-light'],
            },
            success: {
                main: customTheme['success-main'],
                light: customTheme['success-light']
            },
            error: {
                main: customTheme['danger-main'],
                light: customTheme['danger-light']
            },
            info: {
                main: customTheme['info-main'],
                light: customTheme['info-light']
            },
            feedback: {
                success: customTheme['success-main'],
                error: customTheme['error-main'],
                warning: customTheme['warning-feedback'],
                info: customTheme['info-feedback']
            }
        }
    } : {});

    // @ts-expect-error Ignorar ts
    const options: Theme = {
        components: {
            MuiModal: {
                styleOverrides: {
                    root: {
                        zIndex: 10000
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        height: 40
                    }
                },
                variants: [
                    {
                        props: { variant: 'contained', color: 'primary' },
                        style: {
                            letterSpacing: '0%',

                            cursor: 'pointer',
                            padding: '8px 26px',
                            transition: 'all .5s ease-in-out',

                            background: theme.palette.secondary[300],
                            boxShadow: '0px 2px 6px 0px rgba(3, 1, 1, 0.25)',

                            '&:hover': {
                                background: theme.palette.secondary[200],
                            },

                            '&:disabled': {
                                background: theme.palette.secondary[200],
                                color: 'white',
                                opacity: .4
                            }
                        }
                    },
                    {
                        props: { variant: 'outlined' },
                        style: {
                            borderWidth: 2,
                            borderStyle: 'normal',
                            background: 'none',

                            '&:disabled': {
                                background: 'none',
                                cursor: 'default',
                            }
                        }
                    },
                    {
                        props: { variant: 'outlined', color: 'primary' },
                        style: {
                            borderColor: theme.palette.secondary[300],
                            color: theme.palette.secondary[300],

                            '&:hover': {
                                background: theme.palette.secondary[300],
                                borderWidth: 2,
                                borderColor: theme.palette.secondary[300],
                                color: theme.palette.neutral[400],
                            },
                        }
                    },
                    {
                        props: { variant: 'outlined', color: 'error' },
                        style: {
                            borderColor: theme.palette.error.main,
                            color: theme.palette.error.main,
                            fontWeight: 700,

                            '&:hover': {
                                background: theme.palette.error.main,
                                borderColor: theme.palette.error.main,
                                color: theme.palette.neutral[400],
                                borderWidth: 2
                            },
                        }
                    },
                    {
                        props: { variant: 'text' },
                        style: {
                            color: theme.palette.secondary[300],
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5
                        }
                    }
                ]
            },
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        fontSize: '1.4rem !important',
                        backgroundColor: 'transparent',
                        borderRadius: '50px !important',
                    }
                }
            },
            MuiSelect: {
                defaultProps: {
                    style: {
                        marginTop: -6,
                        height: 62,
                        fontSize: "1.6rem",
                        borderRadius: 8,
                    }
                }
            },
            MuiInputBase: {
                defaultProps: {
                    style: {
                        fontSize: "1.6rem",
                        borderRadius: 8,
                    }
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: "1.6rem"
                    }
                }
            },
            MuiFormHelperText: {
                defaultProps: {
                    style: {
                        fontSize: "1.2rem",
                        marginLeft: 0,
                        whiteSpace: "nowrap"
                    }
                }
            },
            MuiCheckbox: {
                defaultProps: {
                    style: {
                        transform: "scale(1.3)"
                    }
                },
                styleOverrides: {
                    root: {
                        // color: '#9e9e9e', // cor padrão quando não marcado
                        '&.Mui-checked': {
                            color: theme.palette.secondary[300],
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 16,
                        padding: 20,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 'calc(100vw - 30px)',

                        "&.MuiDialog-paperWidthXs": {
                            maxWidth: "300px",
                        },
                        "&.MuiDialog-paperWidthSm": {
                            maxWidth: "500px",
                        },
                        "&.MuiDialog-paperWidthMd": {
                            maxWidth: "700px",
                        },
                        "&.MuiDialog-paperWidthLg": {
                            maxWidth: "900px",
                        },
                        "&.MuiDialog-paperWidthXl": {
                            maxWidth: "1200px",
                        },
                    }
                }
            },
            MuiDialogTitle: {
                defaultProps: {
                    style: {
                        fontFamily: 'Nexa, Lato, sans-serif',
                        fontWeight: 700,
                        fontSize: '2rem',
                        paddingBottom: 5,
                        textAlign: 'center',
                        color: theme.palette.neutral[200]
                    }
                }
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        fontSize: "1.6rem",
                        padding: 0,
                        textAlign: 'center',
                    },
                }
            },
            MuiDialogActions: {
                defaultProps: {
                    style: {
                        marginTop: 15,
                    }
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        [theme.breakpoints.down("sm")]: {
                            flexDirection: 'column-reverse',
                            width: '100%',
                            gap: 20,

                            "& .MuiButton-root": {
                                [theme.breakpoints.down("sm")]: {
                                    width: "100%",
                                    marginLeft: 0
                                },
                            },
                        }
                    })
                }
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        fontSize: '1.4rem',
                        padding: '6px 24px',
                        borderRadius: 8,
                        color: `${theme.palette.neutral[400]} !important`
                    },
                    icon: {
                        display: 'flex',
                        alignItems: 'center'
                    },
                    action: {
                        display: 'flex',
                        alignItems: 'center',
                        padding: 0,
                    }
                },
                variants: [
                    {
                        props: { variant: 'filled', severity: 'info' },
                        style: {
                            background: theme.palette.primary.main,
                        }
                    },
                    {
                        props: { variant: 'filled', severity: 'warning' },
                        style: {
                            background: theme.palette.feedback.warning,
                        }
                    }
                ]
            },
            MuiCard: {
                defaultProps: {
                    style: {
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 16,
                        padding: '24px',
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    head: {
                        backgroundColor: theme.palette.background.secondary,
                        padding: 16,
                        fontWeight: 700,

                        '&:first-of-type': {
                            borderBottomLeftRadius: 8
                        },
                        '&:last-of-type': {
                            borderBottomRightRadius: 8
                        }
                    },
                    root: {
                        fontSize: "1.4rem",
                        whiteSpace: 'normal',
                        width: 'fit-content'
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    head: {
                        height: 'fit-content',
                    },
                    root: {
                        height: 80
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontSize: '1.2rem'
                    }
                },
                variants: [
                    {
                        props: { variant: 'filled', color: 'info' },
                        style: {
                            backgroundColor: theme.palette.primary[800],
                            color: theme.palette.neutral[100]
                        }
                    },
                    {
                        props: { variant: 'filled', color: 'secondary' },
                        style: {
                            backgroundColor: theme.palette.grey[800],
                            color: theme.palette.neutral[100]
                        }
                    },
                    {
                        props: { variant: 'filled', color: 'warning' },
                        style: {
                            backgroundColor: theme.palette.warning.light,
                            color: theme.palette.neutral[100]
                        }
                    },
                    {
                        props: { variant: 'filled', color: 'success' },
                        style: {
                            backgroundColor: theme.palette.success.light,
                            color: theme.palette.neutral[100]
                        }
                    },
                    {
                        props: { variant: 'filled', color: 'error' },
                        style: {
                            backgroundColor: theme.palette.error.light,
                            color: theme.palette.neutral[100]
                        }
                    }
                ]
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: theme.palette.grey[500],
                        fontSize: '1rem'
                    }
                }
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        height: 4,
                        background: `linear-gradient(326deg, ${theme.palette.secondary.main} -5.54%, ${theme.palette.primary.main} 100%)`
                    }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        textTransform: 'none',
                        padding: '20px',
                        color: theme.palette.grey[400],
                        fontWeight: 700,
                        borderRadius: '0 !important',

                        "&.Mui-selected": {
                            color: theme.palette.grey[400]
                        }
                    }),
                }
            },
            MuiBadge: {
                variants: [
                    {
                        props: { variant: 'dot' },
                        style: {
                            background: theme.palette.secondary[300],
                            padding: '4px 7px',
                            color: theme.palette.neutral[400],
                            borderRadius: 25,
                            width: 22,
                            fontSize: '1.2rem',
                            textAlign: 'center',
                            marginLeft: 5
                        }
                    }
                ]
            },
            MuiDivider: {
                variants: [
                    {
                        props: { className: 'gradient' },
                        style: {
                            height: '2px',
                            margin: '10px 0',
                            background: `linear-gradient(326deg, ${theme.palette.secondary.main} -5.54%, ${theme.palette.primary.main} 100%)`,
                        }
                    }
                ]
            },
            MuiListItem: {
                styleOverrides: {
                    root: {
                        paddingLeft: 0,
                        borderRadius: 8,
                        marginBottom: 8,
                        border: `2px solid white`,

                        '&:last-of-type': {
                            marginBottom: 0
                        },

                        '&.active': {
                            borderColor: theme.palette.secondary.main,
                        }
                    }
                }
            }
        }
    }

    return createTheme(theme, ptBR, options);
}